/**
 * One dense line in a bounded set, as a three-zone grid.
 *
 * A flex chain lets every row negotiate its own column widths, which is why
 * rows in one set used to measure differently from each other. Here the
 * template is fixed — `auto minmax(0, 1fr) auto` — and exactly one zone is
 * allowed to shrink: the identity. The trailing zone is a shared collapsible
 * slot, never a reserved column, so a row without a figure gives that space
 * back to its title rather than holding an empty gutter.
 *
 * "Allowed to shrink" is not "allowed to vanish", which is why the identity can
 * declare a floor. Without one the zone's minimum is zero, so a trailing zone
 * wide enough takes the item's own name down to an ellipsis and keeps its own
 * figure whole.
 *
 * The zones' cross-axis alignment is likewise a declaration rather than a
 * constant: centring is right for a one-line row and is exactly what stops a
 * lead ornament from spanning a two-line one.
 *
 * At Mobile the compact action moves to the identity's second line. It is
 * never deleted: a row that loses its action at 350px is a row a reader cannot
 * act on.
 *
 * The row publishes its own rung. It paints no surface, but it is a nesting
 * level: without it a 40px lead thumb inside a bounded set resolves against the
 * PLATE's child rung and lands at 12px, which on a 40px square is a lozenge.
 * Publishing the inset rung puts a row's own thumb on `CRC_R_THUMB`, the rung
 * named for exactly that.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { crcTransition, CrcAnimatedProperty, CrcDuration, CrcEase } from "./cresultcard.motion";
import { CresultcardRadiusProvider } from "./cresultcard.radius";
import {
  CRC_DIVIDER,
  CRC_NUMERIC_STYLE,
  CRC_R_INSET,
  CRC_ROW_IDENTITY_FLOOR,
  CRC_TEXT_FAINT,
} from "./cresultcard.tokens";
import {
  CresultcardRowIdentityFloor,
  CresultcardRowZoneAlign,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";
import { CresultcardOpenHint } from "./CresultcardOpenHint";

const ROW_INLINE_PAD = 13;
const ROW_BLOCK_PAD = 9;
const ROW_GAP = 10;
const RANK_COLUMN_WIDTH = 14;

/**
 * One declaration for all three zones, so a row cannot centre its copy against
 * a lead ornament it has asked to span.
 */
const ZONE_ALIGN_ITEMS: Record<
  CresultcardRowZoneAlign,
  CSSProperties["alignItems"]
> = {
  [CresultcardRowZoneAlign.Center]: "center",
  [CresultcardRowZoneAlign.Stretch]: "stretch",
};

interface CresultcardRowProps {
  /**
   * The family's own name for the composition this row IS, stamped for the
   * audit tooling exactly as `CresultcardShell` stamps a card's. Rows render no
   * shell, so without it a whole layout is unidentifiable on the page — which
   * is what blocked a measurement pass over the Rows tier.
   */
  readonly variant: string;
  /** Rank numeral, mark or a fixed-square thumb. */
  readonly lead?: ReactNode;
  /** The only zone allowed to shrink. */
  readonly identity: ReactNode;
  /**
   * The floor under that shrinking. Without one a wide trailing zone squeezes
   * the item's own name to nothing, and the name is the thing the row is for.
   */
  readonly identityFloor?: CresultcardRowIdentityFloor;
  /**
   * How the three zones sit against each other on the cross axis. `Stretch` is
   * what a lead ornament spanning both lines of a two-line row needs.
   */
  readonly zoneAlign?: CresultcardRowZoneAlign;
  /** Tertiary metadata, as a second line inside the identity zone. */
  readonly meta?: ReactNode;
  /** The row's headline figure. */
  readonly figure?: ReactNode;
  /** At most one compact action. */
  readonly action?: ReactNode;
  /** The open hint is always the last element in the trailing zone. */
  readonly openHint?: boolean;
  readonly onLast?: boolean;
  /** The vendor's rank, rendered as the row's own index so order is never implied. */
  readonly rank?: number;
}

export function CresultcardRow({
  variant,
  lead,
  identity,
  identityFloor = CresultcardRowIdentityFloor.None,
  zoneAlign = CresultcardRowZoneAlign.Center,
  meta,
  figure,
  action,
  openHint = false,
  onLast = false,
  rank,
}: CresultcardRowProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const actionInTrailingZone = action != null && !isMobile;
  const actionUnderIdentity = action != null && isMobile;
  const identityMinimum = CRC_ROW_IDENTITY_FLOOR[identityFloor];
  const zoneAlignItems = ZONE_ALIGN_ITEMS[zoneAlign];

  return (
    <CresultcardRadiusProvider radius={CRC_R_INSET}>
      <div
        className="cresultcard__row"
        data-el="Cresultcard/Row/Root"
        data-crc-variant={variant}
        style={{
          display: "grid",
          gridTemplateColumns: `auto minmax(${String(identityMinimum)}px, 1fr) auto`,
          alignItems: zoneAlignItems,
          gap: ROW_GAP,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          paddingBlock: ROW_BLOCK_PAD,
          paddingInline: ROW_INLINE_PAD,
          borderBottom: onLast ? undefined : `1px solid ${CRC_DIVIDER}`,
          transition: crcTransition({
            properties: [CrcAnimatedProperty.BackgroundColor],
            duration: CrcDuration.Hover,
            ease: CrcEase.Settle,
          }),
        }}
      >
        <div
          data-el="Cresultcard/Row/Lead"
          style={{
            display: "flex",
            alignItems: zoneAlignItems,
            gap: 8,
            flex: "0 0 auto",
          }}
        >
          {rank != null && (
            <span
              style={{
                ...CRC_NUMERIC_STYLE,
                width: RANK_COLUMN_WIDTH,
                flex: "0 0 auto",
                // One glyph in a fixed column: it is centred on the row whatever
                // the zones do, because a rank is never the spanning ornament.
                alignSelf: "center",
                fontSize: 10,
                fontWeight: 700,
                textAlign: "end",
                color: CRC_TEXT_FAINT,
              }}
            >
              {rank}
            </span>
          )}
          {lead}
        </div>

        <div
          data-el="Cresultcard/Row/Identity"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            minWidth: 0,
          }}
        >
          {identity}
          {meta}
          {actionUnderIdentity && (
            <div
              style={{ display: "flex", paddingTop: 2 }}
            >
              {action}
            </div>
          )}
        </div>

        <div
          data-el="Cresultcard/Row/Trailing"
          style={{
            display: "flex",
            alignItems: zoneAlignItems,
            gap: 9,
            flex: "0 0 auto",
            minWidth: 0,
          }}
        >
          {figure}
          {actionInTrailingZone && action}
          {openHint && (
            <CresultcardOpenHint />
          )}
        </div>
      </div>
    </CresultcardRadiusProvider>
  );
}
